import { useEffect, useState } from "react";
import { Loader2, ShoppingBag, Ticket, QrCode } from "lucide-react";
import { api } from "../../lib/api";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const FULFILL_LABEL = { awaiting_fulfillment: "Preparing your order", sent_to_printer: "In production", shipped: "Shipped", delivered: "Delivered", "n/a": "" };

export const PurchasesSection = () => {
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/members/purchases").then((r) => setOrders(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Unable to load purchases"));
  }, []);
  if (err) return <p className="text-black/50 py-10 text-center">{err}</p>;
  if (!orders) return <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div>;
  return (
    <div data-testid="purchases-section" className="space-y-3">
      {orders.map((o) => (
        <div key={o.id} className="bg-white border border-black/10 rounded-xl p-5" data-testid="purchase-row">
          <div className="flex justify-between items-start gap-3 flex-wrap">
            <div>
              <p className="font-mono font-bold text-sm">{o.order_number}</p>
              <p className="text-xs text-black/45">{new Date(o.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
            </div>
            <div className="text-right">
              <p className="font-display font-bold">{money(o.total)}</p>
              {o.refund_status !== "none" && <span className="text-xs text-[#E63946]">{o.refund_status === "full" ? "Refunded" : "Partially refunded"}</span>}
            </div>
          </div>
          <div className="mt-3 space-y-1">
            {(o.items || []).map((i, idx) => (
              <div key={idx} className="flex justify-between text-sm"><span className="text-black/70">{i.name} × {i.qty}</span><span>{money(i.unit * i.qty)}</span></div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-black/45">
            {Number(o.discount) > 0 && <span>Discount: -{money(o.discount)}{o.promo_code ? ` (${o.promo_code})` : ""}</span>}
            {Number(o.service_fee) > 0 && <span>Fee: {money(o.service_fee)}</span>}
            {Number(o.shipping) > 0 && <span>Shipping: {money(o.shipping)}</span>}
            {Number(o.tax) > 0 && <span>Tax: {money(o.tax)}</span>}
          </div>
          {FULFILL_LABEL[o.fulfillment_status] && (
            <p className="mt-2 text-xs font-medium text-[#E63946]">{FULFILL_LABEL[o.fulfillment_status]}{o.tracking ? ` · Tracking: ${o.tracking}` : ""}</p>
          )}
        </div>
      ))}
      {orders.length === 0 && (
        <div className="py-14 text-center text-black/40">
          <ShoppingBag className="mx-auto mb-3 text-[#E63946]" size={30} />
          <p>No purchases yet. Orders placed with your verified email appear here.</p>
        </div>
      )}
    </div>
  );
};

export const TicketsSection = () => {
  const [tickets, setTickets] = useState(null);
  const [err, setErr] = useState("");
  const [showQr, setShowQr] = useState(null);
  useEffect(() => {
    api.get("/members/tickets").then((r) => setTickets(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Unable to load tickets"));
  }, []);
  if (err) return <p className="text-black/50 py-10 text-center">{err}</p>;
  if (!tickets) return <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div>;
  return (
    <div data-testid="tickets-section" className="space-y-3">
      {tickets.map((t, i) => (
        <div key={t.ticket_code || i} className="bg-white border border-black/10 rounded-xl p-5" data-testid="ticket-row">
          <div className="flex justify-between items-start gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <Ticket size={20} className="text-[#E63946] shrink-0 mt-0.5" />
              <div>
                <p className="font-display font-bold">{t.event_title}</p>
                <p className="text-xs text-black/45 mt-0.5">
                  {t.event_date ? new Date(t.event_date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : ""}
                  {t.event_time ? ` · ${t.event_time}` : ""}{t.event_location ? ` · ${t.event_location}` : ""}
                </p>
                <p className="text-xs text-black/45 mt-0.5">{t.quantity} ticket{t.quantity > 1 ? "s" : ""} · <span className="font-mono">{t.ticket_code}</span>{t.checked_in ? " · Checked in ✓" : ""}</p>
              </div>
            </div>
            <button onClick={() => setShowQr(showQr === t.ticket_code ? null : t.ticket_code)} data-testid="show-ticket-qr-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] text-white px-4 py-2 text-sm hover:bg-[#E63946] transition-colors"><QrCode size={14} /> {showQr === t.ticket_code ? "Hide" : "Show"} Ticket</button>
          </div>
          {showQr === t.ticket_code && (
            <div className="mt-4 text-center border-t border-black/10 pt-4">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/paypal/tickets/qr/${t.ticket_code}.png`} alt="Ticket QR" className="w-44 h-44 mx-auto border border-black/10 rounded-xl p-2" />
              <p className="text-xs text-black/45 mt-2">Show this at the door</p>
            </div>
          )}
        </div>
      ))}
      {tickets.length === 0 && (
        <div className="py-14 text-center text-black/40">
          <Ticket className="mx-auto mb-3 text-[#E63946]" size={30} />
          <p>No tickets yet. Event tickets purchased with your verified email appear here.</p>
        </div>
      )}
    </div>
  );
};
